import { useState, useEffect } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CalendarClock,
  Loader2,
  RefreshCw,
  Play,
  Eye,
  Users,
  CheckCircle2,
  XCircle,
  Power,
} from "lucide-react";

export function AdminWaCronPanel({ setPinAction }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // "toggle" | "backfill" | "run" | "dry"
  const [data, setData] = useState(null);
  const [dryPreview, setDryPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.waCronStatus();
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load cron status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await adminAPI.waCronStatus();
        if (!active) return;
        setData(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load cron status");
      }
    })();
    return () => { active = false; };
  }, []);

  const handleToggle = () => {
    if (busy) return;
    const newVal = !data?.settings?.enabled;
    const confirmMsg = newVal
      ? "Enable WA cron? Reminders will start firing on the next hourly tick."
      : "Disable WA cron? No further reminders will be sent.";
    if (!window.confirm(confirmMsg)) return;
    setPinAction({
      label: newVal ? "Enable WA cron" : "Disable WA cron",
      fn: async (pin) => {
        setBusy("toggle");
        try {
          const res = await adminAPI.waCronToggle(newVal, pin);
          toast.success(res.data.enabled ? "Cron enabled" : "Cron disabled");
          await load();
        } finally { setBusy(null); }
      },
    });
  };

  const handleBackfill = () => {
    if (busy) return;
    if (!window.confirm(
      "One-time backfill: assign staggered stage_entered_at to ALL " +
        (data?.counts?.awaiting_backfill ?? "?") +
        " users. Cannot be undone. Proceed?"
    )) return;
    setPinAction({
      label: "Backfill existing users",
      fn: async (pin) => {
        setBusy("backfill");
        try {
          const res = await adminAPI.waCronBackfill(pin);
          toast.success(`Backfilled ${res.data.updated ?? 0} users`);
          await load();
        } finally { setBusy(null); }
      },
    });
  };

  const handleDryRun = () => {
    if (busy) return;
    setPinAction({
      label: "Dry-run WA cron",
      fn: async (pin) => {
        setBusy("dry");
        setDryPreview(null);
        try {
          const res = await adminAPI.waCronRunNow(true, pin);
          setDryPreview(res.data);
          toast.success(`Dry run: ${res.data.sent} would be sent`);
        } finally { setBusy(null); }
      },
    });
  };

  const handleRunNow = () => {
    if (busy) return;
    if (!window.confirm(
      "Fire the cron immediately for ALL eligible users right now? This sends real WhatsApp messages."
    )) return;
    setPinAction({
      label: "Run WA cron NOW",
      fn: async (pin) => {
        setBusy("run");
        try {
          const res = await adminAPI.waCronRunNow(false, pin);
          toast.success(`Cron: ${res.data.sent} sent, ${res.data.failed} failed`);
          await load();
        } finally { setBusy(null); }
      },
    });
  };

  const enabled = !!data?.settings?.enabled;
  const backfillDone = !!data?.settings?.backfill_done;
  const counts = data?.counts || {};
  const last = data?.settings?.last_run_summary;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="wa-cron-panel">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-semibold text-[#18181B]">WA Cron — Stage-Day Scheduler</span>
          <StateBadge enabled={enabled} />
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="wa-cron-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading && !data ? (
        <div className="p-6 flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="p-4 space-y-3">
          {/* Count grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <CountCard label="Free users" value={counts.free_users_total} icon={Users} />
            <CountCard label="WA opted-in" value={counts.opted_in} />
            <CountCard label="Ready (staged)" value={counts.with_stage_entered_at} highlight={enabled} />
            <CountCard label="Awaiting backfill" value={counts.awaiting_backfill} warn={!backfillDone && counts.awaiting_backfill > 0} />
          </div>

          {/* Action row */}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleToggle}
              disabled={busy !== null}
              className={enabled ? "bg-red-600 hover:bg-red-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="wa-cron-toggle-btn"
            >
              {busy === "toggle" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Power className="h-3.5 w-3.5 mr-1.5" />}
              {enabled ? "Disable Cron" : "Enable Cron"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBackfill}
              disabled={busy !== null || backfillDone}
              data-testid="wa-cron-backfill-btn"
              title={backfillDone ? "Already run — one-time only" : ""}
            >
              {busy === "backfill" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Users className="h-3.5 w-3.5 mr-1.5" />}
              {backfillDone ? "Backfill Done" : `Backfill ${counts.awaiting_backfill || 0} Users`}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDryRun} disabled={busy !== null} data-testid="wa-cron-dryrun-btn">
              {busy === "dry" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
              Dry Run Preview
            </Button>
            <Button size="sm" variant="outline" onClick={handleRunNow} disabled={busy !== null} data-testid="wa-cron-run-btn">
              {busy === "run" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
              Run Now
            </Button>
          </div>

          {/* Schedule reference */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px]">
            <p className="font-semibold text-slate-700 mb-1.5">Schedule</p>
            <ul className="space-y-0.5 text-slate-600 font-mono">
              <li>Stage 0 (0 downloads): Day 1, 2, 5, 10, 20 → library CTA</li>
              <li>Stage 1 (1 download):  Day 1, 2, 4, 10, 20 → library CTA</li>
              <li>Stage 2 (2 downloads): Day 1, 2, 4, 6, 10, 20 → upgrade CTA</li>
            </ul>
            <p className="text-slate-500 mt-1.5">
              Day 1 fires 1h after entry; Day N (N≥2) fires (N-1) days after entry. 24h cooldown between any two sends. Hourly tick fires at :05 minutes past.
            </p>
          </div>

          {/* Last run summary */}
          {last && (
            <div className="rounded-lg border border-slate-200 p-3" data-testid="wa-cron-last-run">
              <p className="text-[11px] font-semibold text-slate-700 mb-1.5">Last run</p>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-[11px]">
                <StatMini label="scanned" value={last.eligible_scanned} />
                <StatMini label="sent" value={last.sent} color="text-emerald-700" />
                <StatMini label="cooldown" value={last.skipped_cooldown} />
                <StatMini label="no match" value={last.skipped_no_match} />
                <StatMini label="no stage" value={last.skipped_no_stage_entered} />
                <StatMini label="failed" value={last.failed} color={last.failed > 0 ? "text-red-600" : ""} />
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5 font-mono">
                {last.finished_at ? new Date(last.finished_at).toLocaleString() : "—"}
              </p>
              {last.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-[11px] cursor-pointer text-slate-500">
                    Errors ({last.errors.length})
                  </summary>
                  <ul className="mt-1 text-[10px] font-mono text-red-600 space-y-0.5 max-h-32 overflow-auto">
                    {last.errors.map((e, i) => (<li key={i}>{e}</li>))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {/* Dry preview */}
          {dryPreview && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="wa-cron-dry-preview">
              <p className="text-[11px] font-semibold text-blue-800 mb-1.5">
                Dry run — {dryPreview.sent} users would be sent right now
              </p>
              <ul className="text-[10px] font-mono text-slate-700 space-y-0.5 max-h-40 overflow-auto">
                {(dryPreview.errors || []).map((e, i) => (<li key={i}>{e}</li>))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StateBadge({ enabled }) {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="h-3 w-3" /> Enabled
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600">
      <XCircle className="h-3 w-3" /> Disabled
    </span>
  );
}

function CountCard({ label, value, icon: Icon, highlight, warn }) {
  const border = warn ? "border-amber-300 bg-amber-50" : highlight ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-lg border p-2.5 ${border}`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon className="h-3 w-3 text-slate-500" />}
        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-lg font-bold text-[#18181B]">{value ?? "—"}</p>
    </div>
  );
}

function StatMini({ label, value, color = "text-slate-700" }) {
  return (
    <div>
      <p className="text-[9px] text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm font-bold ${color}`}>{value ?? 0}</p>
    </div>
  );
}
